import React, { useEffect, useMemo, useRef, useState } from 'react';
import './index.css';

const ZH = {
  brand: 'SUCODE VR LAB',
  title: '落球粘度 VR / AR 实验',
  subtitle: '实时数字孪生实验台 · AI 助手 · 远程观测',
  entryTitle: '实验入口',
  simulateEntry: '模拟实验',
  simulateHint: '面向 AR / VR 模拟、AI 助手、实验参数展示。',
  realEntry: '真实实验',
  realHint: '面向镜头位姿、小球跟踪、网站数字孪生和真实实验监控。',
  sessionsEntry: '实验 Sessions',
  sessionsHint: '查看、上传和管理所有实验 session，包括轨迹回放和 COMSOL 分析结果。',
  downloadEntry: '客户端下载',
  downloadHint: '下载 Windows 桌面端实验平台或 iOS TestFlight 客户端。',
  online: '设备在线',
  offline: '等待 iPhone 上线',
  temp: '温度',
  viscosity: '粘度',
  reynolds: '雷诺数',
  velocity: '速度',
  fluid: '液体',
  density: '液体密度',
  placement: '放置状态',
  calibration: '校准状态',
  mode: '运行模式',
  calibrated: '已校准',
  notCalibrated: '未校准',
  realtime: '实时',
  slowMotion: '慢放',
  latestExperiment: '最近完成实验',
  noExperiment: '还没有收到完成实验记录',
  experimentId: '编号',
  duration: '耗时',
  distance: '测程',
  result: '结果',
  verdict: '判定',
  valid: '有效',
  invalidPrefix: '无效：',
  voiceAssistant: '语音 AI 助手',
  voiceHint: '点“开始说话”或点下面快捷问题，AI 会结合当前实时实验数据回答。',
  voiceAvailable: '语音可用',
  voiceUnavailable: '当前浏览器不支持语音识别',
  startListening: '开始说话',
  stopListening: '停止录音',
  sendToAi: '发送给 AI',
  currentCommand: '当前指令',
  aiReply: 'AI 回复',
  aiThinking: 'AI 正在生成回复…',
  aiWelcome: '欢迎来到实验助手。你可以直接问“为什么雷诺数变化了”“现在换成了什么液体”“给我下一步操作建议”。',
  aiDefaultError: 'AI 服务暂时不可用',
  aiEmptyReply: '我已收到指令，但暂时没有生成有效回复。',
  voiceErrorPrefix: '语音识别失败：',
  stream: '实时事件流',
  trend: '实时曲线',
  digitalTwin: '真实实验数字孪生',
  digitalTwinHint: '镜头位姿、小球局部 3D 位置、视频预览全部在网页里实时联动。',
  livePreview: '手机实时预览',
  livePreviewHint: 'WebRTC 低延迟完整预览，默认不裁切手机竖屏画面。',
  scanResult: '扫描结果',
  cloudSessions: '云端实验会话',
  cloudSessionsHint: '手机端或未来 EXE 上传后的实验摘要会保存在这里，便于学生跨端回看。',
  noSessions: '还没有上传的实验会话',
  frameTime: '最新帧时间',
  scanBaseline: '基线偏移',
  scanRadius: '球半径像素',
  scanHeight: '量筒高度',
  velocityTrend: '速度曲线',
  reynoldsTrend: '雷诺数曲线',
  viscosityTrend: '粘度曲线',
  tempTrend: '温度曲线',
  noData: '等待实时数据',
  prompt1: '分析当前实验数据',
  prompt2: '为什么现在的雷诺数是这个值',
  prompt3: '当前液体和温度对结果有什么影响',
  prompt4: '给我下一步操作建议',
  connectVps: '连接 VPS',
  disconnectVps: '断开 VPS',
  connectPhone: '连接手机',
  vpsStatus: 'VPS 状态',
  phoneStatus: '手机状态',
  pairingCode: '配对码',
  pairingHint: '在 iPhone 实验 App 中输入以下配对码',
  pairingWaiting: '等待手机配对...',
  pairingPaired: '已配对',
  pairingIdle: '未配对',
  requestPairing: '请求配对码',
  wsConnected: '已连接',
  wsDisconnected: '未连接',
  wsConnecting: '连接中...',
  wsError: '连接错误',
};

const panelStyle = {
  borderRadius: '28px',
  background: 'rgba(7, 18, 34, 0.82)',
  border: '1px solid rgba(125, 211, 252, 0.14)',
  boxShadow: '0 22px 80px rgba(0, 0, 0, 0.34), inset 0 0 40px rgba(34, 211, 238, 0.05)'
};

const muted = 'rgba(186, 230, 253, 0.72)';

function MetricCard({ label, value, accent, subvalue }) {
  return (
    <div style={{ padding: '18px 18px 16px', borderRadius: '22px', background: 'rgba(3, 10, 21, 0.84)', border: `1px solid ${accent}22` }}>
      <div style={{ fontSize: '12px', color: muted, marginBottom: '8px', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, color: '#f8fafc' }}>{value}</div>
      {subvalue ? <div style={{ marginTop: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.52)' }}>{subvalue}</div> : null}
    </div>
  );
}

function buttonStyle(primary) {
  return {
    border: '1px solid rgba(125, 211, 252, 0.18)',
    background: primary ? 'linear-gradient(135deg, rgba(8, 145, 178, 0.95), rgba(59, 130, 246, 0.92))' : 'rgba(7, 20, 38, 0.72)',
    color: 'rgba(255,255,255,0.92)',
    borderRadius: '14px',
    padding: '12px 16px',
    cursor: 'pointer',
    fontWeight: 700
  };
}

const chipStyle = {
  border: '1px solid rgba(125, 211, 252, 0.18)',
  background: 'rgba(7, 20, 38, 0.72)',
  color: 'rgba(255,255,255,0.82)',
  borderRadius: '999px',
  padding: '8px 12px',
  cursor: 'pointer'
};

function ChartCard({ title, color, values, formatter }) {
  const width = 320;
  const height = 140;
  const safeValues = values.filter((value) => Number.isFinite(value));
  const latest = safeValues.length ? safeValues[safeValues.length - 1] : null;

  const path = useMemo(() => {
    if (safeValues.length < 2) return '';
    const min = Math.min(...safeValues);
    const max = Math.max(...safeValues);
    const span = Math.max(max - min, 1e-6);
    return safeValues
      .map((value, index) => {
        const x = (index / (safeValues.length - 1)) * width;
        const y = height - ((value - min) / span) * (height - 14) - 7;
        return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(' ');
  }, [safeValues]);

  const fillPath = useMemo(() => {
    if (!path) return '';
    return `${path} L ${width} ${height} L 0 ${height} Z`;
  }, [path]);

  return (
    <div style={{ padding: '18px', borderRadius: '22px', background: 'rgba(3, 10, 21, 0.84)', border: '1px solid rgba(125, 211, 252, 0.12)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px' }}>
        <div style={{ fontSize: '14px', color: muted }}>{title}</div>
        <div style={{ fontSize: '13px', color: '#f8fafc', fontWeight: 700 }}>{latest == null ? '--' : formatter(latest)}</div>
      </div>
      {safeValues.length > 1 ? (
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '140px', display: 'block' }}>
          <defs>
            <linearGradient id={`fill-${title}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.35" />
              <stop offset="100%" stopColor={color} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {[0.2, 0.5, 0.8].map((ratio) => (
            <line key={ratio} x1="0" x2={width} y1={height * ratio} y2={height * ratio} stroke="rgba(148,163,184,0.14)" strokeDasharray="4 6" />
          ))}
          <path d={fillPath} fill={`url(#fill-${title})`} />
          <path d={path} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <div style={{ height: '140px', display: 'grid', placeItems: 'center', color: 'rgba(255,255,255,0.45)' }}>{ZH.noData}</div>
      )}
    </div>
  );
}

function formatMetric(value, digits, unit = '') {
  return Number.isFinite(value) ? `${Number(value).toFixed(digits)}${unit}` : '--';
}

function formatTimestamp(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString('zh-CN', { hour12: false });
}

function firstFinite(...values) {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return NaN;
}

function clamp01(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function RealTwinPanel({ latestAr, latestFrame, latestCalib, statusText, statusTone }) {
  const camera = latestAr?.camera || {};
  const camX = firstFinite(camera.px, latestAr?.cam_px);
  const camY = firstFinite(camera.py, latestAr?.cam_py);
  const camZ = firstFinite(camera.pz, latestAr?.cam_pz);
  const cylinderHeight = firstFinite(latestAr?.cylinder_height_m, latestCalib?.cylinder_height_m, 0.38);
  const ballLocalMeters = firstFinite(
    latestAr?.ball_local_y,
    latestAr?.position,
    latestAr?.ballY
  );
  const fallRatio = Number.isFinite(ballLocalMeters) && cylinderHeight > 0
    ? clamp01(1 - ballLocalMeters / cylinderHeight)
    : 0.18;
  const confidence = firstFinite(latestAr?.confidence, latestAr?.ball_confidence, latestFrame?.confidence);
  const source = latestAr?._sourceLabel || latestFrame?._sourceLabel || '等待数据';
  const ballTop = `${10 + fallRatio * 74}%`;
  const rows = [
    ['镜头 X/Y/Z', [camX, camY, camZ].map((value) => formatMetric(value, 3)).join(' / ')],
    ['小球局部高度', Number.isFinite(ballLocalMeters) ? formatMetric(ballLocalMeters, 4, ' m') : '--'],
    ['速度', latestAr ? formatMetric(latestAr.velocity, 4, ' m/s') : '--'],
    ['识别置信度', Number.isFinite(confidence) ? `${Math.round(confidence * 100)}%` : '--'],
    ['数据源', source]
  ];

  return (
    <div style={{ borderRadius: '22px', overflow: 'hidden', border: '1px solid rgba(125, 211, 252, 0.12)', background: 'linear-gradient(145deg, rgba(2, 8, 18, 0.92), rgba(8, 31, 46, 0.68))', minHeight: '520px', padding: '20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.1fr', gap: '18px', minHeight: '480px' }}>
        <div style={{ display: 'grid', gap: '14px', alignContent: 'start' }}>
          <div style={{ padding: '18px', borderRadius: '18px', background: 'rgba(3, 10, 22, 0.82)', border: '1px solid rgba(125, 211, 252, 0.14)' }}>
            <div style={{ color: 'rgba(125, 211, 252, 0.78)', fontSize: '12px', letterSpacing: '0.16em', marginBottom: '8px' }}>REAL DIGITAL TWIN</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <strong style={{ fontSize: '24px' }}>Godot 跟随状态</strong>
              <span style={{ color: statusTone, border: `1px solid ${statusTone}55`, borderRadius: '999px', padding: '7px 10px', fontSize: '12px', fontWeight: 800 }}>{statusText}</span>
            </div>
            <div style={{ marginTop: '10px', color: muted, lineHeight: 1.7 }}>网页只保留轻量状态；真实 3D 画面由本地 Godot/EXE 接管，避免在主站重复渲染造成延迟。</div>
          </div>

          <div style={{ display: 'grid', gap: '10px', padding: '16px', borderRadius: '18px', background: 'rgba(3, 10, 22, 0.72)', border: '1px solid rgba(125, 211, 252, 0.12)' }}>
            {rows.map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', borderBottom: label === '数据源' ? 'none' : '1px solid rgba(125, 211, 252, 0.08)', paddingBottom: label === '数据源' ? 0 : '10px' }}>
                <span style={{ color: muted }}>{label}</span>
                <strong style={{ textAlign: 'right' }}>{value}</strong>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'relative', minHeight: '480px', borderRadius: '24px', overflow: 'hidden', background: 'radial-gradient(circle at 50% 18%, rgba(34, 211, 238, 0.22), transparent 34%), linear-gradient(180deg, rgba(7, 20, 38, 0.9), rgba(2, 8, 18, 0.96))', border: '1px solid rgba(125, 211, 252, 0.14)' }}>
          <div style={{ position: 'absolute', inset: '18px', borderRadius: '22px', backgroundImage: 'linear-gradient(rgba(125, 211, 252, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(125, 211, 252, 0.08) 1px, transparent 1px)', backgroundSize: '36px 36px', opacity: 0.5 }} />
          <div style={{ position: 'absolute', left: '50%', top: '9%', width: '36%', height: '78%', transform: 'translateX(-50%)', borderRadius: '999px 999px 30px 30px', border: '2px solid rgba(186, 230, 253, 0.5)', background: 'linear-gradient(90deg, rgba(125, 211, 252, 0.12), rgba(255,255,255,0.16), rgba(125, 211, 252, 0.08))', boxShadow: 'inset 0 0 38px rgba(125, 211, 252, 0.18)' }} />
          {[18, 34, 50, 66, 82].map((top) => (
            <div key={top} style={{ position: 'absolute', left: '34%', right: '34%', top: `${top}%`, height: '2px', background: 'rgba(255,255,255,0.46)', boxShadow: '0 0 14px rgba(125, 211, 252, 0.28)' }} />
          ))}
          <div style={{ position: 'absolute', left: '50%', top: ballTop, width: '36px', height: '36px', transform: 'translate(-50%, -50%)', borderRadius: '50%', background: 'radial-gradient(circle at 35% 30%, #fff, #d7dde4 48%, #8894a5 100%)', boxShadow: '0 0 26px rgba(255,255,255,0.38), 0 16px 30px rgba(0,0,0,0.35)' }} />
          <div style={{ position: 'absolute', left: '50%', bottom: '9%', width: '56%', height: '18px', transform: 'translateX(-50%)', borderRadius: '50%', background: 'rgba(100, 244, 172, 0.58)', filter: 'blur(0.2px)', boxShadow: '0 0 22px rgba(100, 244, 172, 0.42)' }} />
          <div style={{ position: 'absolute', left: '18px', right: '18px', bottom: '18px', display: 'flex', justifyContent: 'space-between', color: muted, fontSize: '12px' }}>
            <span>量筒高度 {formatMetric(cylinderHeight, 3, ' m')}</span>
            <span>帧 {formatTimestamp(latestFrame?.timestamp || latestAr?.timestamp)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const LIVE_ENDPOINTS = [
  {
    name: 'single-iphone',
    label: '单 iPhone',
    state: '/api/single-iphone/state',
    stream: '/api/single-iphone/stream',
    rank: 2
  },
  {
    name: 'legacy-ar',
    label: '兼容 AR',
    state: '/api/ar/state',
    stream: '/api/ar/stream',
    rank: 1
  }
];

function packetTime(value) {
  if (!value) return 0;
  const raw = value.timestamp || value.createdAt || value.updatedAt;
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : 0;
}

function markSource(payload, endpoint) {
  if (!payload || typeof payload !== 'object') return payload;
  return {
    ...payload,
    _sourceName: endpoint.name,
    _sourceLabel: endpoint.label,
    _sourceRank: endpoint.rank
  };
}

function chooseLiveValue(current, incoming) {
  if (!incoming) return current || null;
  if (!current) return incoming;
  const currentRank = Number(current._sourceRank || 0);
  const incomingRank = Number(incoming._sourceRank || 0);
  const currentTime = packetTime(current);
  const incomingTime = packetTime(incoming);
  if (incomingRank > currentRank && incomingTime + 15000 >= currentTime) return incoming;
  if (incomingTime >= currentTime) return incoming;
  return current;
}

function mergeLivePacket(prev, packet, endpoint) {
  const taggedLatest = markSource(packet?.latest || (packet?.type === 'telemetry' ? packet : null), endpoint);
  const taggedFrame = markSource(packet?.lastFrame || (packet?.type === 'frame' ? packet : null), endpoint);
  const taggedCalib = markSource(packet?.lastCalib || (packet?.type === 'calib' ? packet : null), endpoint);
  const nextLatest = chooseLiveValue(prev.latest, taggedLatest);
  const nextFrame = chooseLiveValue(prev.lastFrame, taggedFrame);
  const nextCalib = chooseLiveValue(prev.lastCalib, taggedCalib);
  const incomingHistory = Array.isArray(packet?.history)
    ? packet.history.map((item) => markSource(item, endpoint))
    : [];
  const canUseIncomingHistory =
    incomingHistory.length > 0 &&
    (!prev.history?.length ||
      Number(incomingHistory[0]?._sourceRank || 0) >= Number(prev.history[0]?._sourceRank || 0) ||
      packetTime(incomingHistory[0]) >= packetTime(prev.history[0]));

  return {
    latest: nextLatest,
    history: canUseIncomingHistory ? incomingHistory : (prev.history || []),
    lastFrame: nextFrame,
    lastCalib: nextCalib
  };
}

export default function App() {
  const [arFeed, setArFeed] = useState({ latest: null, history: [], lastFrame: null, lastCalib: null });
  const [cloudSessions, setCloudSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSessionDetail, setSelectedSessionDetail] = useState(null);
  const [entryMode, setEntryMode] = useState('simulate');
  const [sessionViewId, setSessionViewId] = useState(null);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceInput, setVoiceInput] = useState('');
  const [assistantReply, setAssistantReply] = useState(ZH.aiWelcome);
  const [chatHistory, setChatHistory] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [pairingStatus, setPairingStatus] = useState('idle');
  const [pairingCode, setPairingCode] = useState('');
  const [pairingCountdown, setPairingCountdown] = useState(0);
  const [wsErrorMsg, setWsErrorMsg] = useState('');
  const wsRef = useRef(null);
  const pairingTimerRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const streams = [];

    LIVE_ENDPOINTS.forEach((endpoint) => {
      fetch(`${endpoint.state}?t=${Date.now()}`, { cache: 'no-store' })
        .then((res) => res.json())
        .then((data) => {
          if (mounted) {
            setArFeed((prev) => mergeLivePacket(prev, data, endpoint));
          }
        })
        .catch(() => {});

      const stream = new EventSource(endpoint.stream);
      stream.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setArFeed((prev) => mergeLivePacket(prev, data, endpoint));
        } catch (error) {
          console.error(`${endpoint.name} stream parse error`, error);
        }
      };
      stream.onerror = () => {};
      streams.push(stream);
    });

    return () => {
      mounted = false;
      streams.forEach((stream) => stream.close());
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    fetch('/api/platform/sessions')
      .then((res) => res.json())
      .then((data) => {
        if (mounted) {
          const sessions = data?.sessions || [];
          setCloudSessions(sessions);
          if (sessions.length) {
            setSelectedSession(sessions[0]);
          }
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!selectedSession?.sessionId) {
      setSelectedSessionDetail(null);
      return undefined;
    }
    fetch(`/api/platform/sessions/${selectedSession.sessionId}`)
      .then((res) => res.json())
      .then((data) => {
        if (mounted) setSelectedSessionDetail(data?.session || null);
      })
      .catch(() => {
        if (mounted) setSelectedSessionDetail(null);
      });
    return () => {
      mounted = false;
    };
  }, [selectedSession?.sessionId]);

  useEffect(() => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      setVoiceSupported(false);
      return undefined;
    }

    const recognition = new Recognition();
    recognition.lang = 'zh-CN';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsListening(true);
      setAiError('');
    };

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript || '')
        .join('')
        .trim();
      setVoiceInput(transcript);
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = (event) => {
      setIsListening(false);
      setAiError(ZH.voiceErrorPrefix + event.error);
    };

    recognitionRef.current = recognition;
    setVoiceSupported(true);

    return () => {
      recognition.stop();
      recognitionRef.current = null;
    };
  }, []);

  // Auth state
const [authUser, setAuthUser] = useState(() => { try { return JSON.parse(localStorage.getItem("sucode_user")); } catch { return null; } });
const [authToken, setAuthToken] = useState(() => localStorage.getItem("sucode_token") || "");
const [authMode, setAuthMode] = useState("login");
const [authLoading, setAuthLoading] = useState(false);
const [authError, setAuthError] = useState("");
const [loginEmail, setLoginEmail] = useState("");
const [loginPassword, setLoginPassword] = useState("");
const [regEmail, setRegEmail] = useState("");
const [regPassword, setRegPassword] = useState("");
const [regUsername, setRegUsername] = useState("");
const [regRole, setRegRole] = useState("student");
const handleLogin = async (e) => { e.preventDefault(); setAuthLoading(true); setAuthError(""); try { const res = await fetch("/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: loginEmail, password: loginPassword }) }); const data = await res.json(); if (data.ok) { localStorage.setItem("sucode_token", data.token); localStorage.setItem("sucode_user", JSON.stringify(data.user)); setAuthToken(data.token); setAuthUser(data.user); } else { setAuthError(data.error || "登录失败"); } } catch { setAuthError("网络错误"); } setAuthLoading(false); };
const handleRegister = async (e) => { e.preventDefault(); setAuthLoading(true); setAuthError(""); try { const res = await fetch("/api/auth/register", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: regEmail, password: regPassword, username: regUsername, role: regRole }) }); const data = await res.json(); if (data.ok) { localStorage.setItem("sucode_token", data.token); localStorage.setItem("sucode_user", JSON.stringify(data.user)); setAuthToken(data.token); setAuthUser(data.user); } else { setAuthError(data.error || "注册失败"); } } catch { setAuthError("网络错误"); } setAuthLoading(false); };
const handleLogout = () => { localStorage.removeItem("sucode_token"); localStorage.removeItem("sucode_user"); setAuthToken(""); setAuthUser(null); };
const roleText = (r) => ({ student: "学生", teacher: "老师", admin: "管理员" })[r] || r;
const roleColor = (r) => ({ student: "#81c784", teacher: "#ffb74d", admin: "#ef5350" })[r] || "#fff";

const latestAr = arFeed.latest;
  const latestFrame = arFeed.lastFrame;
  const latestCalib = arFeed.lastCalib;
  const latestExperiment = latestAr?.experiment || null;
  const history = useMemo(() => [...(arFeed.history || [])].reverse(), [arFeed.history]);
  const historyForCharts = history.slice(-96);
  const liveAge = latestAr ? Date.now() - packetTime(latestAr) : Infinity;
  const isFreshLive = latestAr && liveAge < 10000;
  const statusTone = isFreshLive ? '#7ee787' : latestAr ? '#ffd166' : '#7dd3fc';
  const statusText = latestAr
    ? `${isFreshLive ? ZH.online : '最近同步'} · ${latestAr._sourceLabel || '实时链路'} · ${(latestAr.experimentState || 'idle').toUpperCase()}`
    : ZH.offline;

  const metrics = [
    { label: ZH.temp, value: latestAr ? formatMetric(latestAr.temperature, 1, ' °C') : '--', accent: '#fb7185' },
    { label: ZH.viscosity, value: latestAr ? formatMetric(latestAr.viscosity, 4, ' Pa·s') : '--', accent: '#38bdf8' },
    { label: ZH.reynolds, value: latestAr ? formatMetric(latestAr.reynolds, 2) : '--', accent: '#7dd3fc', subvalue: '网页端已经接入实时雷诺数' },
    { label: ZH.velocity, value: latestAr ? formatMetric(latestAr.velocity, 4, ' m/s') : '--', accent: '#22d3ee' },
    { label: ZH.fluid, value: latestAr?.fluidName || '--', accent: '#a78bfa', subvalue: latestAr?.fluidId || '等待液体数据' },
    { label: ZH.density, value: latestAr ? formatMetric(latestAr.fluidDensity, 0, ' kg/m³') : '--', accent: '#f59e0b' }
  ];

  const speak = (text) => {
    if (!('speechSynthesis' in window) || !text) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'zh-CN';
    utterance.rate = 1.05;
    window.speechSynthesis.speak(utterance);
  };

  const askAssistant = async (message) => {
    const input = message.trim();
    if (!input || aiBusy) return;

    setAiBusy(true);
    setAiError('');
    const nextHistory = chatHistory.concat([{ role: 'user', content: input }]);
    setChatHistory(nextHistory);

    try {
      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: input, history: nextHistory })
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || ZH.aiDefaultError);
      }
      const reply = data.reply || ZH.aiEmptyReply;
      setAssistantReply(reply);
      setChatHistory((prev) => prev.concat([{ role: 'assistant', content: reply }]).slice(-12));
      speak(reply);
    } catch (error) {
      setAiError(error.message);
    } finally {
      setAiBusy(false);
    }
  };


  // WebSocket pairing functions
  const getStudentId = () => {
    return authUser?.student_id || authUser?.id || authUser?.email || 'anonymous';
  };

  const cleanupWs = () => {
    if (pairingTimerRef.current) {
      clearInterval(pairingTimerRef.current);
      pairingTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const connectWs = () => {
    if (wsRef.current) return;
    setWsErrorMsg('');
    const studentId = getStudentId();
    const ws = new WebSocket('wss://sucode.top/tracker');
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      ws.send(JSON.stringify({
        type: 'identify',
        student_id: studentId,
        role: 'browser',
        client: 'web_student_v0.1.0'
      }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        switch (msg.type) {
          case 'identify_ok':
            console.log('[WS] Identified:', msg.student_id);
            break;
          case 'pair_code':
            setPairingCode(msg.code || '');
            setPairingStatus('waiting');
            setPairingCountdown(msg.expires_in || 300);
            if (pairingTimerRef.current) clearInterval(pairingTimerRef.current);
            pairingTimerRef.current = setInterval(() => {
              setPairingCountdown((c) => {
                if (c <= 1) {
                  clearInterval(pairingTimerRef.current);
                  pairingTimerRef.current = null;
                  return 0;
                }
                return c - 1;
              });
            }, 1000);
            break;
          case 'pairing_status':
            setPairingStatus(msg.status || 'idle');
            if (msg.status === 'paired') {
              setPairingCode('');
              if (pairingTimerRef.current) {
                clearInterval(pairingTimerRef.current);
                pairingTimerRef.current = null;
              }
            }
            break;
          case 'server_status':
            if (msg.pairing_status) {
              setPairingStatus(msg.pairing_status);
            }
            break;
          case 'pair_success':
            setPairingStatus('paired');
            setPairingCode('');
            if (pairingTimerRef.current) {
              clearInterval(pairingTimerRef.current);
              pairingTimerRef.current = null;
            }
            break;
          case 'error':
            setWsErrorMsg(msg.message || 'Unknown error');
            break;
        }
      } catch (e) {
        console.error('[WS] Parse error:', e);
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      setPairingStatus('idle');
      wsRef.current = null;
    };

    ws.onerror = (e) => {
      setWsConnected(false);
      setWsErrorMsg('WebSocket 连接失败');
      wsRef.current = null;
    };
  };

  const disconnectWs = () => {
    cleanupWs();
    setWsConnected(false);
    setPairingStatus('idle');
    setPairingCode('');
    setPairingCountdown(0);
  };

  const requestPairingCode = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: 'pair',
      action: 'request',
      student_id: getStudentId()
    }));
  };

  useEffect(() => {
    return () => { cleanupWs(); };
  }, []);

  const startListening = () => {
    if (!recognitionRef.current || isListening) return;
    setVoiceInput('');
    recognitionRef.current.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
  };

  const quickPrompts = [ZH.prompt1, ZH.prompt2, ZH.prompt3, ZH.prompt4];
  const entryCard = (mode, title, hint) => ({
    cursor: 'pointer',
    padding: '18px 20px',
    borderRadius: '22px',
    background: entryMode === mode ? 'linear-gradient(135deg, rgba(8, 145, 178, 0.32), rgba(59, 130, 246, 0.24))' : 'rgba(3, 10, 22, 0.76)',
    border: entryMode === mode ? '1px solid rgba(125, 211, 252, 0.42)' : '1px solid rgba(125, 211, 252, 0.12)',
    boxShadow: entryMode === mode ? '0 16px 40px rgba(34, 211, 238, 0.12)' : 'none'
  });

  if (!authToken) {
    return (
      <div style={{ minHeight: '100vh', background: 'radial-gradient(circle at top left, #0a1628 0%, #0d1f33 50%, #0a1929 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif' }}>
        <div style={{ width: '100%', maxWidth: 420, padding: 20 }}>
          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 24, padding: '36px 28px', backdropFilter: 'blur(20px)' }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <h1 style={{ fontSize: 24, fontWeight: 700, background: 'linear-gradient(90deg, #4fc3f7, #81c784)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 }}>SUCODE LAB</h1>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginTop: 4 }}>落球法粘度实验平台</p>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 24, background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: 4 }}>
              <button onClick={() => { setAuthMode('login'); setAuthError(''); }} style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: authMode === 'login' ? 'rgba(79,195,247,0.15)' : 'transparent', color: authMode === 'login' ? '#4fc3f7' : 'rgba(255,255,255,0.5)', fontSize: 14, fontWeight: authMode === 'login' ? 700 : 500, cursor: 'pointer' }}>登录</button>
              <button onClick={() => { setAuthMode('register'); setAuthError(''); }} style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: authMode === 'register' ? 'rgba(79,195,247,0.15)' : 'transparent', color: authMode === 'register' ? '#4fc3f7' : 'rgba(255,255,255,0.5)', fontSize: 14, fontWeight: authMode === 'register' ? 700 : 500, cursor: 'pointer' }}>注册</button>
            </div>
            {authMode === 'login' ? (
              <form onSubmit={handleLogin}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 6, fontWeight: 500 }}>邮箱</label>
                  <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required placeholder="your@email.com" style={{ width: '100%', padding: '12px 14px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 14, outline: 'none' }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 6, fontWeight: 500 }}>密码</label>
                  <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required placeholder="至少6位" style={{ width: '100%', padding: '12px 14px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 14, outline: 'none' }} />
                </div>
                <button type="submit" disabled={authLoading} style={{ width: '100%', padding: 14, border: 'none', borderRadius: 12, background: 'linear-gradient(135deg, #4fc3f7, #29b6f6)', color: '#0a1628', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: authLoading ? 0.5 : 1 }}>{authLoading ? '登录中...' : '登录'}</button>
                {authError && <p style={{ color: '#ef5350', fontSize: 13, marginTop: 12, textAlign: 'center' }}>{authError}</p>}
              </form>
            ) : (
              <form onSubmit={handleRegister}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 6, fontWeight: 500 }}>邮箱</label>
                  <input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} required placeholder="your@email.com" style={{ width: '100%', padding: '12px 14px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 14, outline: 'none' }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 6, fontWeight: 500 }}>用户名</label>
                  <input type="text" value={regUsername} onChange={(e) => setRegUsername(e.target.value)} required placeholder="您的姓名" style={{ width: '100%', padding: '12px 14px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 14, outline: 'none' }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 6, fontWeight: 500 }}>密码</label>
                  <input type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required placeholder="至少6位" style={{ width: '100%', padding: '12px 14px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 14, outline: 'none' }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 6, fontWeight: 500 }}>角色</label>
                  <select value={regRole} onChange={(e) => setRegRole(e.target.value)} style={{ width: '100%', padding: '12px 14px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 14, outline: 'none' }}>
                    <option value="student" style={{ background: '#0a1628' }}>学生</option>
                    <option value="teacher" style={{ background: '#0a1628' }}>老师</option>
                  </select>
                </div>
                <button type="submit" disabled={authLoading} style={{ width: '100%', padding: 14, border: 'none', borderRadius: 12, background: 'linear-gradient(135deg, #4fc3f7, #29b6f6)', color: '#0a1628', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: authLoading ? 0.5 : 1 }}>{authLoading ? '注册中...' : '注册'}</button>
                {authError && <p style={{ color: '#ef5350', fontSize: 13, marginTop: 12, textAlign: 'center' }}>{authError}</p>}
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app" style={{ minHeight: '100vh', background: 'radial-gradient(circle at top left, #314d73 0%, #1a2338 36%, #0b1017 100%)', color: 'rgba(255,255,255,0.92)' }}>
      <main style={{ maxWidth: '1480px', margin: '0 auto', padding: '30px 24px 40px' }}>
        <div style={{ ...panelStyle, padding: '26px 28px', marginBottom: '24px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at right top, rgba(125, 211, 252, 0.14), transparent 34%)' }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '12px', letterSpacing: '0.28em', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '10px' }}>{ZH.brand}</div>
              <h1 style={{ margin: 0, fontSize: '34px', fontWeight: 800 }}>{ZH.title}</h1>
              <div style={{ marginTop: '10px', color: 'rgba(226, 232, 240, 0.72)' }}>{ZH.subtitle}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {authUser && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '999px', background: 'rgba(3, 10, 20, 0.82)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #4fc3f7, #81c784)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#0a1628' }}>{authUser.username?.[0]?.toUpperCase() || 'U'}</div>
                <span style={{ fontSize: 13, color: '#fff' }}>{authUser.username}</span>
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: roleColor(authUser.role) + '22', color: roleColor(authUser.role) }}>{roleText(authUser.role)}</span>
                <button onClick={handleLogout} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer', marginLeft: 4 }}>退出</button>
              </div>
            )}
            <div style={{ padding: '12px 16px', borderRadius: '999px', background: 'rgba(3, 10, 20, 0.82)', border: `1px solid ${statusTone}`, color: statusTone, fontWeight: 700 }}>{statusText}</div>
          </div>
          </div>
        </div>

        <div style={{ ...panelStyle, padding: '22px', marginBottom: '24px' }}>
          <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '14px' }}>{ZH.entryTitle}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px' }}>
            <div style={entryCard('simulate')} onClick={() => setEntryMode('simulate')}>
              <div style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>{ZH.simulateEntry}</div>
              <div style={{ color: 'rgba(226, 232, 240, 0.7)', lineHeight: 1.7 }}>{ZH.simulateHint}</div>
            </div>
            <div style={entryCard('real')} onClick={() => setEntryMode('real')}>
              <div style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>{ZH.realEntry}</div>
              <div style={{ color: 'rgba(226, 232, 240, 0.7)', lineHeight: 1.7 }}>{ZH.realHint}</div>
            </div>
            <div style={entryCard('sessions')} onClick={() => { setEntryMode('sessions'); setSessionViewId(null); }}>
              <div style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>{ZH.sessionsEntry}</div>
              <div style={{ color: 'rgba(226, 232, 240, 0.7)', lineHeight: 1.7 }}>{ZH.sessionsHint}</div>
            </div>
            <div style={entryCard('downloads')} onClick={() => setEntryMode('downloads')}>
              <div style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>{ZH.downloadEntry}</div>
              <div style={{ color: 'rgba(226, 232, 240, 0.7)', lineHeight: 1.7 }}>{ZH.downloadHint}</div>
            </div>
          </div>
        </div>

        {entryMode === 'real' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1.08fr 0.92fr', gap: '24px' }}>
            <section style={{ display: 'grid', gap: '24px' }}>
              {/* Pairing Control Panel */}
              <div style={{ ...panelStyle, padding: '22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '4px' }}>设备配对</div>
                    <div style={{ color: 'rgba(226, 232, 240, 0.66)', fontSize: '13px' }}>通过配对码将 iPhone 与当前网页绑定</div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.55)' }}>{ZH.vpsStatus}</span>
                      <span style={{
                        color: wsConnected ? '#7ee787' : '#fca5a5',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: wsConnected ? '#7ee787' : '#fca5a5',
                          display: 'inline-block'
                        }} />
                        {wsConnected ? ZH.wsConnected : ZH.wsDisconnected}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.55)' }}>{ZH.phoneStatus}</span>
                      <span style={{
                        color: pairingStatus === 'paired' ? '#7ee787' : pairingStatus === 'waiting' ? '#ffd166' : '#fca5a5',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: pairingStatus === 'paired' ? '#7ee787' : pairingStatus === 'waiting' ? '#ffd166' : '#fca5a5',
                          display: 'inline-block'
                        }} />
                        {pairingStatus === 'paired' ? ZH.pairingPaired : pairingStatus === 'waiting' ? ZH.pairingWaiting : ZH.pairingIdle}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {!wsConnected ? (
                    <button onClick={connectWs} style={buttonStyle(true)}>{ZH.connectVps}</button>
                  ) : (
                    <button onClick={disconnectWs} style={buttonStyle(false)}>{ZH.disconnectVps}</button>
                  )}
                  {wsErrorMsg ? (
                    <span style={{ color: '#fca5a5', fontSize: '13px' }}>{wsErrorMsg}</span>
                  ) : null}
                </div>
                {wsConnected && pairingStatus !== 'paired' ? (
                  <div style={{
                    marginTop: '14px',
                    padding: '14px 18px',
                    borderRadius: '14px',
                    background: 'rgba(250, 204, 21, 0.08)',
                    border: '1px solid rgba(250, 204, 21, 0.2)',
                    color: '#facc15',
                    fontSize: '14px',
                    fontWeight: 600
                  }}>
                    已连接 VPS，等待 iPhone 进入真实实验...
                  </div>
                ) : null}
                {pairingStatus === 'paired' ? (
                  <div style={{
                    marginTop: '14px',
                    padding: '14px 18px',
                    borderRadius: '14px',
                    background: 'rgba(126, 231, 135, 0.08)',
                    border: '1px solid rgba(126, 231, 135, 0.2)',
                    color: '#7ee787',
                    fontSize: '14px',
                    fontWeight: 600
                  }}>
                    iPhone 已同步！实时数据将在下方显示。
                  </div>
                ) : null}
              </div>

              <div style={{ ...panelStyle, padding: '22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '16px', marginBottom: '14px' }}>
                  <div>
                    <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '8px' }}>{ZH.digitalTwin}</div>
                    <div style={{ color: 'rgba(226, 232, 240, 0.66)', fontSize: '13px' }}>{ZH.digitalTwinHint}</div>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.56)', fontSize: '12px' }}>
                    {latestAr?.camera ? `镜头 ${(latestAr.camera.px || 0).toFixed(3)}, ${(latestAr.camera.py || 0).toFixed(3)}, ${(latestAr.camera.pz || 0).toFixed(3)}` : '等待镜头位姿'}
                  </div>
                </div>
                <RealTwinPanel
                  latestAr={latestAr}
                  latestFrame={latestFrame}
                  latestCalib={latestCalib}
                  statusText={statusText}
                  statusTone={statusTone}
                />
              </div>

              <div style={{ ...panelStyle, padding: '24px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '14px' }}>{ZH.trend}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                  <ChartCard title={ZH.velocityTrend} color="#22d3ee" values={historyForCharts.map((item) => item.velocity)} formatter={(value) => `${value.toFixed(4)} m/s`} />
                  <ChartCard title={ZH.reynoldsTrend} color="#7dd3fc" values={historyForCharts.map((item) => item.reynolds)} formatter={(value) => value.toFixed(2)} />
                  <ChartCard title={ZH.viscosityTrend} color="#38bdf8" values={historyForCharts.map((item) => item.viscosity)} formatter={(value) => `${value.toFixed(4)} Pa·s`} />
                  <ChartCard title={ZH.tempTrend} color="#fb7185" values={historyForCharts.map((item) => item.temperature)} formatter={(value) => `${value.toFixed(1)} °C`} />
                </div>
              </div>
            </section>

            <section style={{ display: 'grid', gap: '24px' }}>
              <div style={{ ...panelStyle, padding: '22px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '12px' }}>{ZH.livePreview}</div>
                <div style={{ color: 'rgba(226, 232, 240, 0.66)', fontSize: '13px', marginBottom: '14px' }}>{ZH.livePreviewHint}</div>
                <div style={{ borderRadius: '22px', overflow: 'hidden', border: '1px solid rgba(125, 211, 252, 0.12)', background: 'rgba(2, 8, 18, 0.72)' }}>
                  <iframe
                    title="手机实时预览"
                    src="/realtime/preview.html?v=20260502-webrtc"
                    style={{ width: '100%', height: 'min(78vh, 760px)', minHeight: '620px', border: 0, display: 'block', background: 'rgba(2, 8, 18, 0.72)' }}
                  />
                </div>
                <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'space-between', gap: '16px', color: 'rgba(255,255,255,0.72)', fontSize: '13px', alignItems: 'center' }}>
                  <span>{ZH.frameTime}</span>
                  <strong>{formatTimestamp(latestFrame?.timestamp)}</strong>
                  <a href="/realtime/preview.html?v=20260502-webrtc" target="_blank" rel="noreferrer" style={{ color: '#7dd3fc', fontWeight: 700 }}>打开完整预览</a>
                </div>
              </div>

              <div style={{ ...panelStyle, padding: '22px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '12px' }}>{ZH.scanResult}</div>
                <div style={{ display: 'grid', gap: '10px', padding: '18px 20px', borderRadius: '22px', background: 'rgba(3, 10, 22, 0.82)', border: '1px solid rgba(125, 211, 252, 0.16)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                    <span style={{ color: muted }}>{ZH.calibration}</span>
                    <span>{latestAr?.isCalibrated ? ZH.calibrated : ZH.notCalibrated}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                    <span style={{ color: muted }}>{ZH.scanBaseline}</span>
                    <span>{latestCalib ? Number(latestCalib.baseline_y || 0).toFixed(4) : '--'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                    <span style={{ color: muted }}>{ZH.scanRadius}</span>
                    <span>{latestCalib ? Number(latestCalib.r0_px || 0).toFixed(2) : '--'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                    <span style={{ color: muted }}>{ZH.scanHeight}</span>
                    <span>{latestCalib ? `${Number(latestCalib.cylinder_height_m || 0).toFixed(3)} m` : '--'}</span>
                  </div>
                </div>
              </div>

              <div style={{ ...panelStyle, padding: '22px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '8px' }}>{ZH.cloudSessions}</div>
                <div style={{ color: 'rgba(226, 232, 240, 0.66)', fontSize: '13px', marginBottom: '14px' }}>{ZH.cloudSessionsHint}</div>
                <div style={{ display: 'grid', gap: '10px', maxHeight: '260px', overflow: 'auto' }}>
                  {cloudSessions.length ? cloudSessions.slice(0, 8).map((session) => (
                    <button
                      key={session.sessionId}
                      type="button"
                      onClick={() => setSelectedSession(session)}
                      style={{
                        padding: '14px 16px',
                        borderRadius: '18px',
                        background: selectedSession?.sessionId === session.sessionId ? 'linear-gradient(135deg, rgba(8, 145, 178, 0.24), rgba(59, 130, 246, 0.18))' : 'rgba(2, 8, 18, 0.72)',
                        border: selectedSession?.sessionId === session.sessionId ? '1px solid rgba(125, 211, 252, 0.34)' : '1px solid rgba(125, 211, 252, 0.08)',
                        textAlign: 'left',
                        color: 'inherit',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '8px' }}>
                        <strong>{(session.title || session.liquidName || session.sessionId)}</strong>
                        <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>{formatTimestamp(session.createdAt)}</span>
                      </div>
                      <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>
                        {`${session.mode === 'real' ? '真实实验' : '模拟实验'} · ${session.metadata?.fluidName || '--'} · ${formatMetric(session.analysis?.estimatedViscosity, 4, ' Pa·s')} · Re ${Number(session.analysis?.meanReynolds || 0).toFixed(2)}`}
                      </div>
                    </button>
                  )) : (
                    <div style={{ color: 'rgba(255,255,255,0.45)' }}>{ZH.noSessions}</div>
                  )}
                </div>
                <div style={{ marginTop: '14px', padding: '16px', borderRadius: '18px', background: 'rgba(2, 8, 18, 0.72)', border: '1px solid rgba(125, 211, 252, 0.08)' }}>
                  {selectedSession ? (
                    <div style={{ display: 'grid', gap: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.76)' }}>
                      <strong style={{ fontSize: '15px', color: '#f8fafc' }}>{selectedSession.title || selectedSession.sessionId}</strong>
                      <div>{`会话编号：${selectedSession.sessionId}`}</div>
                      <div>{`模式：${selectedSession.mode === 'real' ? '真实实验' : '模拟实验'}`}</div>
                      <div>{`液体：${(selectedSession.liquidName || selectedSession.metadata?.fluidName || '--')}`}</div>
                      <div>{`粘度：${formatMetric((selectedSession.analysis?.estimatedViscosityPaS ?? selectedSession.analysis?.estimatedViscosity), 4, ' Pa·s')}`}</div>
                      <div>{`平均雷诺数：${Number((selectedSession.analysis?.reynoldsNumber ?? selectedSession.analysis?.meanReynolds ?? 0)).toFixed(2)}`}</div>
                      <div>{`轨迹点数：${selectedSessionDetail?.dataPoints?.length ?? selectedSessionDetail?.frames?.length ?? selectedSession.preview?.sampleCount ?? 0}`}</div>
                      {selectedSessionDetail?.dataPoints?.length ? (
                        <SessionTrajectoryPanel session={selectedSessionDetail} compact />
                      ) : null}
                      {selectedSessionDetail?.artifacts ? (
                        <div style={{ marginTop: '6px', display: 'grid', gap: '6px', color: 'rgba(125, 211, 252, 0.84)' }}>
                          <div>{`分析文件：${selectedSessionDetail.artifacts.analysisCsv || '--'}`}</div>
                          <div>{`会话文件：${selectedSessionDetail.artifacts.sessionJson || '--'}`}</div>
                          <div>{`回放文件：${selectedSessionDetail.artifacts.replayJson || '--'}`}</div>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div style={{ color: 'rgba(255,255,255,0.45)' }}>选择一条会话后可查看摘要详情。</div>
                  )}
                </div>
              </div>

              <div style={{ ...panelStyle, padding: '28px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px', marginBottom: '22px' }}>
                  {metrics.map((metric) => <MetricCard key={metric.label} {...metric} />)}
                </div>

                <div style={{ padding: '18px 20px', borderRadius: '22px', background: 'rgba(3, 10, 22, 0.82)', border: '1px solid rgba(125, 211, 252, 0.16)' }}>
                  {[
                    [ZH.placement, latestAr ? latestAr.placementHint || '--' : ZH.offline],
                    [ZH.calibration, latestAr?.isCalibrated ? ZH.calibrated : ZH.notCalibrated],
                    [ZH.mode, latestAr?.isSlowMotion ? ZH.slowMotion : ZH.realtime],
                    [ZH.fluid, latestAr?.fluidName || '--']
                  ].map(([label, value], index) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: index === 3 ? 0 : '10px' }}>
                      <span style={{ color: muted }}>{label}</span>
                      <span style={{ textAlign: 'right' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ ...panelStyle, padding: '22px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '12px' }}>{ZH.stream}</div>
                <div style={{ display: 'grid', gap: '10px', maxHeight: '520px', overflow: 'auto' }}>
                  {history.slice(-14).reverse().map((item) => (
                    <div key={item.id} style={{ padding: '14px 16px', borderRadius: '18px', background: 'rgba(2, 8, 18, 0.72)', border: '1px solid rgba(125, 211, 252, 0.08)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '6px' }}>
                        <strong>{item.experimentState || 'idle'}</strong>
                        <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', textAlign: 'right' }}>{formatTimestamp(item.timestamp)}</span>
                      </div>
                      <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>
                        {`${ZH.fluid} ${item.fluidName || '--'} · ${ZH.temp} ${item.temperature.toFixed(1)} °C · ${ZH.velocity} ${item.velocity.toFixed(4)} m/s · ${ZH.reynolds} ${item.reynolds.toFixed(2)}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1.16fr 0.94fr', gap: '24px' }}>
          <section style={{ display: 'grid', gap: '24px' }}>
            <div style={{ ...panelStyle, padding: '28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px', marginBottom: '22px' }}>
                {metrics.map((metric) => <MetricCard key={metric.label} {...metric} />)}
              </div>

              <div style={{ padding: '18px 20px', borderRadius: '22px', background: 'rgba(3, 10, 22, 0.82)', border: '1px solid rgba(125, 211, 252, 0.16)' }}>
                {[
                  [ZH.placement, latestAr ? latestAr.placementHint || '--' : ZH.offline],
                  [ZH.calibration, latestAr?.isCalibrated ? ZH.calibrated : ZH.notCalibrated],
                  [ZH.mode, latestAr?.isSlowMotion ? ZH.slowMotion : ZH.realtime],
                  [ZH.fluid, latestAr?.fluidName || '--']
                ].map(([label, value], index) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: index === 3 ? 0 : '10px' }}>
                    <span style={{ color: muted }}>{label}</span>
                    <span style={{ textAlign: 'right' }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ ...panelStyle, padding: '24px' }}>
              <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '14px' }}>{ZH.trend}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                <ChartCard title={ZH.velocityTrend} color="#22d3ee" values={historyForCharts.map((item) => item.velocity)} formatter={(value) => `${value.toFixed(4)} m/s`} />
                <ChartCard title={ZH.reynoldsTrend} color="#7dd3fc" values={historyForCharts.map((item) => item.reynolds)} formatter={(value) => value.toFixed(2)} />
                <ChartCard title={ZH.viscosityTrend} color="#38bdf8" values={historyForCharts.map((item) => item.viscosity)} formatter={(value) => `${value.toFixed(4)} Pa·s`} />
                <ChartCard title={ZH.tempTrend} color="#fb7185" values={historyForCharts.map((item) => item.temperature)} formatter={(value) => `${value.toFixed(1)} °C`} />
              </div>
            </div>
          </section>

          <section style={{ display: 'grid', gap: '24px' }}>
            <div style={{ ...panelStyle, padding: '22px' }}>
              <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '12px' }}>{ZH.latestExperiment}</div>
              {latestExperiment ? (
                <div style={{ display: 'grid', gap: '10px' }}>
                  <div>{ZH.experimentId} #{latestExperiment.id}</div>
                  <div>{ZH.duration} {latestExperiment.deltaT.toFixed(2)} s</div>
                  <div>{ZH.distance} {latestExperiment.deltaH.toFixed(3)} m</div>
                  <div>{ZH.result} {latestExperiment.calculatedViscosity.toFixed(4)} Pa·s</div>
                  <div>{ZH.verdict} {latestExperiment.isValid ? ZH.valid : ZH.invalidPrefix + (latestExperiment.invalidReason || '')}</div>
                </div>
              ) : (
                <div style={{ color: 'rgba(255,255,255,0.55)' }}>{ZH.noExperiment}</div>
              )}
            </div>

            <div style={{ ...panelStyle, padding: '22px 22px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)' }}>{ZH.voiceAssistant}</div>
                <div style={{ fontSize: '12px', color: voiceSupported ? '#7ee787' : 'rgba(255,255,255,0.45)' }}>{voiceSupported ? ZH.voiceAvailable : ZH.voiceUnavailable}</div>
              </div>
              <div style={{ display: 'grid', gap: '14px' }}>
                <div style={{ padding: '16px', borderRadius: '18px', background: 'rgba(2, 8, 18, 0.72)', border: '1px solid rgba(125, 211, 252, 0.08)', minHeight: '92px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(125, 211, 252, 0.72)', marginBottom: '8px' }}>{ZH.currentCommand}</div>
                  <div style={{ fontSize: '16px', lineHeight: 1.7 }}>{voiceInput || ZH.voiceHint}</div>
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button onClick={startListening} disabled={!voiceSupported || isListening} style={buttonStyle(true)}>{ZH.startListening}</button>
                  <button onClick={stopListening} disabled={!isListening} style={buttonStyle(false)}>{ZH.stopListening}</button>
                  <button onClick={() => askAssistant(voiceInput)} disabled={!voiceInput.trim() || aiBusy} style={buttonStyle(false)}>{ZH.sendToAi}</button>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {quickPrompts.map((prompt) => (
                    <button key={prompt} onClick={() => { setVoiceInput(prompt); askAssistant(prompt); }} style={chipStyle}>{prompt}</button>
                  ))}
                </div>
                <div style={{ padding: '16px', borderRadius: '18px', background: 'rgba(2, 8, 18, 0.72)', border: '1px solid rgba(125, 211, 252, 0.08)', minHeight: '144px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(125, 211, 252, 0.72)', marginBottom: '8px' }}>{ZH.aiReply}</div>
                  <div style={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{aiBusy ? ZH.aiThinking : assistantReply}</div>
                  {aiError ? <div style={{ marginTop: '10px', color: '#fca5a5', fontSize: '13px' }}>{aiError}</div> : null}
                </div>
              </div>
            </div>

            <div style={{ ...panelStyle, padding: '22px' }}>
              <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '12px' }}>{ZH.stream}</div>
              <div style={{ display: 'grid', gap: '10px', maxHeight: '320px', overflow: 'auto' }}>
                {history.slice(-12).reverse().map((item) => (
                  <div key={item.id} style={{ padding: '14px 16px', borderRadius: '18px', background: 'rgba(2, 8, 18, 0.72)', border: '1px solid rgba(125, 211, 252, 0.08)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '6px' }}>
                      <strong>{item.experimentState || 'idle'}</strong>
                      <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', textAlign: 'right' }}>{formatTimestamp(item.timestamp)}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>
                      {`${ZH.fluid} ${item.fluidName || '--'} · ${ZH.temp} ${item.temperature.toFixed(1)} °C · ${ZH.velocity} ${item.velocity.toFixed(4)} m/s · ${ZH.reynolds} ${item.reynolds.toFixed(2)}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
        )}

        {entryMode === 'sessions' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>
            {sessionViewId ? (
              <SessionDetail
                sessionId={sessionViewId}
                onBack={() => setSessionViewId(null)}
              />
            ) : (
              <SessionList onSelectSession={(id) => setSessionViewId(id)} />
            )}
          </div>
        )}

        {entryMode === 'downloads' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>
            <div style={{ ...panelStyle, padding: '28px' }}>
              <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '14px' }}>客户端下载</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                <div style={{ background: 'rgba(3, 10, 22, 0.76)', border: '1px solid rgba(125, 211, 252, 0.14)', borderRadius: '18px', padding: '22px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '10px' }}>Windows 桌面端</div>
                  <div style={{ color: 'rgba(226, 232, 240, 0.7)', fontSize: '14px', lineHeight: 1.7, marginBottom: '16px' }}>
                    3D 数字孪生实验场景、实验录制与回放、COMSOL 粘度计算工具<br/>
                    版本: v0.1.0 Preview · 约 2 KB<br/>
                    系统要求: Windows 10/11 64位
                  </div>
                  <a href="/downloads/export.zip" style={{ ...buttonStyle(true), textDecoration: 'none', display: 'inline-block' }}>下载 export.zip</a>
                </div>
                <div style={{ background: 'rgba(3, 10, 22, 0.76)', border: '1px solid rgba(125, 211, 252, 0.14)', borderRadius: '18px', padding: '22px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '10px' }}>iOS 客户端</div>
                  <div style={{ color: 'rgba(226, 232, 240, 0.7)', fontSize: '14px', lineHeight: 1.7, marginBottom: '16px' }}>
                    iPhone 远程服务器管理、SSH 连接、实时实验跟踪<br/>
                    版本: TestFlight Beta · 约 50 MB<br/>
                    系统要求: iOS 16+
                  </div>
                  <a href="https://testflight.apple.com/join/c99SyTaQ" target="_blank" rel="noreferrer" style={{ ...buttonStyle(false), textDecoration: 'none', display: 'inline-block' }}>前往 TestFlight</a>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function SessionTrajectoryPanel({ session, compact = false }) {
  const points = Array.isArray(session?.dataPoints) && session.dataPoints.length
    ? session.dataPoints
    : (Array.isArray(session?.frames) ? session.frames.map((frame) => ({
      t: frame.time,
      y: frame.positionM ?? frame.ball?.yM,
      v: frame.velocityMps,
      reynolds: frame.reynolds,
      viscosity: frame.viscosityPaS
    })) : []);

  const values = (key) => points.map((point) => Number(point[key])).filter(Number.isFinite);
  const latest = points[points.length - 1] || null;

  if (!points.length) {
    return (
      <div style={{ padding: '16px', borderRadius: '18px', background: 'rgba(2, 8, 18, 0.72)', color: 'rgba(255,255,255,0.52)' }}>
        暂无轨迹点。新版手机端上传后会自动生成轨迹、CSV 和 Godot Replay。
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: compact ? '10px' : '16px', marginTop: compact ? '10px' : 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: compact ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '10px' }}>
        <MetricCard label="轨迹点" value={`${points.length}`} accent="#a78bfa" />
        <MetricCard label="末端高度" value={latest?.y == null ? '--' : `${Number(latest.y).toFixed(4)} m`} accent="#22d3ee" />
        {!compact ? <MetricCard label="末端速度" value={latest?.v == null ? '--' : `${Number(latest.v).toFixed(4)} m/s`} accent="#f59e0b" /> : null}
        {!compact ? <MetricCard label="末端雷诺数" value={latest?.reynolds == null ? '--' : Number(latest.reynolds).toFixed(2)} accent="#7dd3fc" /> : null}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
        <ChartCard title="后处理位置曲线" color="#22d3ee" values={values('y')} formatter={(value) => `${value.toFixed(4)} m`} />
        {!compact ? <ChartCard title="后处理速度曲线" color="#f59e0b" values={values('v')} formatter={(value) => `${value.toFixed(4)} m/s`} /> : null}
      </div>
    </div>
  );
}

function SessionList({ onSelectSession }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetch('/api/platform/sessions')
      .then((res) => res.json())
      .then((data) => {
        if (mounted) setSessions(data?.sessions || []);
      })
      .catch(() => {
        if (mounted) setSessions([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div style={{ ...panelStyle, padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'baseline', marginBottom: '18px' }}>
        <div>
          <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '8px' }}>实验 Sessions</div>
          <h2 style={{ margin: 0, fontSize: '28px' }}>云端实验记录与后处理入口</h2>
        </div>
        <div style={{ color: muted }}>{loading ? '加载中' : `${sessions.length} 条记录`}</div>
      </div>
      <div style={{ display: 'grid', gap: '12px' }}>
        {sessions.map((session) => (
          <button
            key={session.sessionId}
            type="button"
            onClick={() => onSelectSession(session.sessionId)}
            style={{
              border: '1px solid rgba(125, 211, 252, 0.14)',
              background: 'rgba(3, 10, 22, 0.78)',
              borderRadius: '22px',
              padding: '18px',
              color: '#f8fafc',
              textAlign: 'left',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '18px', marginBottom: '8px' }}>
              <strong style={{ fontSize: '18px' }}>{session.title || session.sessionId}</strong>
              <span style={{ color: 'rgba(255,255,255,0.48)' }}>{formatTimestamp(session.createdAt)}</span>
            </div>
            <div style={{ color: 'rgba(226,232,240,0.72)', lineHeight: 1.7 }}>
              {`${session.mode === 'real' ? '真实实验' : '模拟实验'} · ${session.metadata?.fluidName || '--'} · 粘度 ${formatMetric(session.analysis?.estimatedViscosity, 4, ' Pa·s')} · Re ${Number(session.analysis?.meanReynolds || 0).toFixed(2)}`}
            </div>
          </button>
        ))}
        {!loading && !sessions.length ? <div style={{ color: 'rgba(255,255,255,0.52)' }}>还没有云端实验记录。</div> : null}
      </div>
    </div>
  );
}

function SessionDetail({ sessionId, onBack }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetch(`/api/platform/sessions/${sessionId}`)
      .then((res) => res.json())
      .then((data) => {
        if (mounted) setSession(data?.session || null);
      })
      .catch(() => {
        if (mounted) setSession(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [sessionId]);

  const csvUrl = `/api/platform/sessions/${sessionId}/export.csv`;
  const replayUrl = `/api/platform/sessions/${sessionId}/replay`;

  return (
    <div style={{ ...panelStyle, padding: '24px' }}>
      <button type="button" onClick={onBack} style={{ ...buttonStyle(false), marginBottom: '18px' }}>返回 Sessions</button>
      {loading ? (
        <div style={{ color: muted }}>正在读取云端会话...</div>
      ) : session ? (
        <div style={{ display: 'grid', gap: '18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '18px', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '14px', color: 'rgba(125, 211, 252, 0.78)', marginBottom: '8px' }}>Session Detail</div>
              <h2 style={{ margin: 0, fontSize: '30px' }}>{session.title || session.sessionId}</h2>
              <div style={{ marginTop: '8px', color: muted }}>{`${session.mode === 'real' ? '真实实验' : '模拟实验'} · ${session.metadata?.fluidName || '--'} · ${session.createdAt || '--'}`}</div>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <a href={csvUrl} style={{ ...buttonStyle(true), textDecoration: 'none' }}>下载 CSV</a>
              <a href={replayUrl} target="_blank" rel="noreferrer" style={{ ...buttonStyle(false), textDecoration: 'none' }}>Godot Replay JSON</a>
            </div>
          </div>
          <SessionTrajectoryPanel session={session} />
        </div>
      ) : (
        <div style={{ color: 'rgba(255,255,255,0.52)' }}>没有找到这个会话。</div>
      )}
    </div>
  );
}
